/**
 * Drosophila connectome abstraction — simplified region-level graph.
 *
 * There is NO language model and no connectome dataset in this file or anywhere
 * in this app. The graph below is 9 hand-written regions joined by 13
 * hand-invented inter-region weights, authored to look plausible. Neuron counts
 * are rounded figures from the published datasets, not per-region measurements.
 *
 * The real data lives elsewhere and is only ever linked to, never shipped:
 * MaleCNS v1.0 (166,700 male neurons, 6.24M connections, Google + HHMI, CC BY 4.0)
 * and FlyWire BANC v888 (158,262 female neurons, FlyWire terms). See
 * `fullConnectome.ts` for their manifests and official viewer URLs.
 *
 * A question string reaches the network only as an FNV-1a hash producing two
 * sensor drive values. Nothing here interprets language.
 */

export type Sex = 'male' | 'female';

export interface BrainRegion {
  id: string;
  name: string;
  /** Approximate neuron count in the real connectome */
  neurons: number;
  /** Normalized 2D position for visualization (x: -1..1, y: -1..1) */
  x: number;
  y: number;
  /** Region role */
  role: 'sensory' | 'processing' | 'learning' | 'motor' | 'modulatory';
  color: string;
}

export const REGIONS: BrainRegion[] = [
  { id: 'eye', name: 'Optic Lobes', neurons: 60000, x: -0.72, y: -0.35, role: 'sensory', color: '#4cc9f0' },
  { id: 'antenna', name: 'Antennal Lobe', neurons: 1000, x: -0.85, y: 0.15, role: 'sensory', color: '#4cc9f0' },
  { id: 'al', name: 'Lateral Horn', neurons: 1300, x: -0.55, y: 0.05, role: 'processing', color: '#b8b8ff' },
  { id: 'mb', name: 'Mushroom Body', neurons: 2200, x: -0.28, y: 0.12, role: 'learning', color: '#ffd166' },
  { id: 'cx', name: 'Central Complex', neurons: 3000, x: 0.05, y: -0.05, role: 'processing', color: '#b8b8ff' },
  { id: 'lpr', name: 'Lateral Protocerebrum', neurons: 4000, x: 0.35, y: 0.1, role: 'processing', color: '#b8b8ff' },
  { id: 'vnc', name: 'Ventral Nerve Cord', neurons: 25000, x: 0.78, y: 0.4, role: 'motor', color: '#06d6a0' },
  { id: 'ppl1', name: 'Dopamine (PPL1)', neurons: 120, x: -0.1, y: 0.45, role: 'modulatory', color: '#ff6b9d' },
  { id: 'mbon', name: 'MB Output Neurons', neurons: 34, x: 0.2, y: 0.42, role: 'motor', color: '#06d6a0' },
];

/**
 * Inter-region edges. The male/female weight multipliers are illustrative
 * choices made by hand for this demo, not values derived from any published
 * connectome — do not cite them as measurements.
 */
export interface RegionEdge {
  from: string;
  to: string;
  /** Male-specific weight multiplier */
  maleWeight: number;
  /** Female-specific weight multiplier */
  femaleWeight: number;
}

export const EDGES: RegionEdge[] = [
  { from: 'eye', to: 'al', maleWeight: 1.0, femaleWeight: 1.0 },
  { from: 'eye', to: 'cx', maleWeight: 1.2, femaleWeight: 0.9 },
  { from: 'antenna', to: 'al', maleWeight: 1.0, femaleWeight: 1.1 },
  { from: 'antenna', to: 'mb', maleWeight: 1.3, femaleWeight: 1.0 },
  { from: 'al', to: 'mb', maleWeight: 1.0, femaleWeight: 1.0 },
  { from: 'al', to: 'lpr', maleWeight: 0.8, femaleWeight: 1.0 },
  { from: 'mb', to: 'mbon', maleWeight: 1.4, femaleWeight: 1.0 },
  { from: 'mb', to: 'lpr', maleWeight: 1.0, femaleWeight: 1.1 },
  { from: 'cx', to: 'lpr', maleWeight: 1.0, femaleWeight: 1.0 },
  { from: 'cx', to: 'vnc', maleWeight: 1.1, femaleWeight: 0.9 },
  { from: 'ppl1', to: 'mb', maleWeight: 1.0, femaleWeight: 1.0 },
  { from: 'mbon', to: 'vnc', maleWeight: 1.2, femaleWeight: 1.0 },
  { from: 'lpr', to: 'vnc', maleWeight: 1.0, femaleWeight: 1.0 },
];

/**
 * Leaky Integrate-and-Fire simulation over the region graph.
 * Each region accumulates input; when it crosses threshold it "fires"
 * and propagates to downstream regions with sex-specific weights.
 */
export interface LifState {
  /** membrane potential per region id */
  v: Record<string, number>;
  /** spike counts per region id */
  spikes: Record<string, number>;
  /** last spike time per region id */
  lastSpike: Record<string, number>;
  /** total simulation steps */
  step: number;
}

export function createLifState(): LifState {
  const v: Record<string, number> = {};
  const spikes: Record<string, number> = {};
  const lastSpike: Record<string, number> = {};
  for (const r of REGIONS) {
    v[r.id] = 0;
    spikes[r.id] = 0;
    lastSpike[r.id] = -1;
  }
  return { v, spikes, lastSpike, step: 0 };
}

const THRESHOLD = 1.0;
const LEAK = 0.85;
const REFRACTORY = 3;

/** Advance one LIF step. `input` drives sensory regions. Returns fired region ids. */
export function stepLif(state: LifState, input: Record<string, number>, sex: Sex): string[] {
  const fired: string[] = [];
  const weightKey = sex === 'male' ? 'maleWeight' : 'femaleWeight';

  // decay + integrate
  for (const r of REGIONS) {
    const prev = state.v[r.id];
    const inSignal = input[r.id] ?? 0;
    const next = prev * LEAK + inSignal;
    state.v[r.id] = next;
  }

  // fire check (with refractory)
  for (const r of REGIONS) {
    const sinceLast = state.step - state.lastSpike[r.id];
    if (state.v[r.id] >= THRESHOLD && sinceLast >= REFRACTORY) {
      fired.push(r.id);
      state.spikes[r.id] += 1;
      state.lastSpike[r.id] = state.step;
      state.v[r.id] = 0; // reset after spike
    }
  }

  // propagate to downstream regions (applied next step via input map)
  const nextInput: Record<string, number> = {};
  for (const e of EDGES) {
    if (fired.includes(e.from)) {
      nextInput[e.to] = (nextInput[e.to] ?? 0) + e[weightKey] * 0.6;
    }
  }
  // write propagated input back into state for the next step call
  for (const r of REGIONS) {
    const prop = nextInput[r.id];
    if (prop) state.v[r.id] += prop;
  }

  state.step += 1;
  return fired;
}

/**
 * Convert a text string into two sensor drive values.
 *
 * FNV-1a hash, nothing more: the text is not read, tokenised, embedded or
 * understood. Two strings that hash alike drive the network alike.
 */
export function questionToInput(question: string): Record<string, number> {
  const input: Record<string, number> = { eye: 0, antenna: 0 };
  const len = Math.min(question.length, 200);
  // deterministic hash-based sensor drive (FNV-1a) — not semantic encoding
  let h = 2166136261;
  for (let i = 0; i < len; i++) {
    h ^= question.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const abs = Math.abs(h);
  input.eye = 0.35 + ((abs % 100) / 100) * 0.5;      // 0.35..0.85
  input.antenna = 0.3 + (((abs >> 7) % 100) / 100) * 0.5;
  return input;
}

/** Run the full simulation for N steps and return spike statistics. */
export function simulate(question: string, sex: Sex, steps = 40): SimResult {
  const state = createLifState();
  const input = questionToInput(question);
  const firedHistory: string[][] = [];
  for (let i = 0; i < steps; i++) {
    // sensory drive only in first 5 steps
    const drive = i < 5 ? input : {};
    firedHistory.push(stepLif(state, drive, sex));
  }
  const totalSpikes = Object.values(state.spikes).reduce((a, b) => a + b, 0);
  return { state, firedHistory, totalSpikes };
}

export interface SimResult {
  state: LifState;
  firedHistory: string[][];
  totalSpikes: number;
}

/**
 * Map spike statistics to a label. These are fixed arithmetic weights over
 * region spike counts — a lookup label, not a decision, an opinion, or a
 * judgement about the text that produced it.
 */
export function spikesToDecision(state: LifState, sex: Sex): FlyDecision {
  const mb = state.spikes['mb'] ?? 0;
  const cx = state.spikes['cx'] ?? 0;
  const mbon = state.spikes['mbon'] ?? 0;
  const vnc = state.spikes['vnc'] ?? 0;
  const eye = state.spikes['eye'] ?? 0;

  const exploration = (mb * 1.4 + eye * 0.3) / 10;
  const caution = (cx * 1.2 + mbon * 0.8) / 10;
  const action = vnc / 10;

  const stance = sex === 'male'
    ? (exploration > caution ? 'AGGRESSIVE-PURSUE' : 'AGGRESSIVE-SCAN')
    : (caution > exploration ? 'CAUTIOUS-EVALUATE' : 'CAUTIOUS-PROBE');

  return {
    stance,
    exploration: Math.min(1, exploration),
    caution: Math.min(1, caution),
    actionReadiness: Math.min(1, action),
    regionSpikes: { ...state.spikes },
  };
}

export interface FlyDecision {
  stance: string;
  exploration: number;
  caution: number;
  actionReadiness: number;
  regionSpikes: Record<string, number>;
}
